import React, { useState, useCallback, useEffect } from 'react';
import { Box, TextField, Select, MenuItem, SelectChangeEvent, Typography, useTheme, Button, IconButton, Tooltip, styled } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import debounce from 'lodash/debounce';
import { keyframes } from '@emotion/react';
import Tips from '../dialogs/Tips';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface GameListFilterProps {
  onSearch: (query: string) => void;
  onOrderChange: (order: string) => void;
  onClearFilter: () => void;
  totalGames: number;
  initialSearchQuery: string;
  initialOrder: string;
  onPriorityModeChange?: (enabled: boolean) => void;
  priorityMode?: boolean;
  currentStatus?: number;
}

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const StyledPriorityButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  borderRadius: '4px',
  padding: '6px',
  transition: 'all 0.2s ease',
  '&.active': {
    background: 'linear-gradient(45deg, #2088ff, #a455ff)',
    backgroundSize: '200% 200%',
    animation: `${gradientAnimation} 3s ease infinite`,
    boxShadow: '0 2px 8px rgba(32, 136, 255, 0.3)',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    '&.active': {
      background: 'linear-gradient(45deg, #2088ff, #a455ff)',
      backgroundSize: '200% 200%',
      animation: `${gradientAnimation} 3s ease infinite`,
      filter: 'brightness(1.1)',
    },
  },
}));

const PriorityTipsContent = () => (
  <Box sx={{ maxWidth: 400 }}>
    <Typography variant="body2" paragraph>
      プライオリティは、積みゲー/中断/ウィッシュリストのゲームに優先度を設定できる機能です。
    </Typography>
    <Typography variant="body2" paragraph>
      使い方：
    </Typography>
    <Box component="ol" sx={{ pl: 2, mt: 1 }}>
      <li>
        <Typography variant="body2" sx={{ mb: 1 }}>
          エディットモード（<TuneIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />）をオンにすると、各ゲームにプライオリティスライダーが表示されます
        </Typography>
      </li>
      <li>
        <Typography variant="body2" sx={{ mb: 1 }}>
          スライダーで0-5の優先度を設定できます
        </Typography>
      </li>
      <li>
        <Typography variant="body2" sx={{ mb: 1 }}>
          プライオリティボタン（<AutoAwesomeIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />）をクリックすると、優先度順に並び替えができます
        </Typography>
      </li>
    </Box>
    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
      ※プレイ済み/プレイ中のゲームではプライオリティ機能は使用できません
    </Typography>
  </Box>
);

const GameListFilter: React.FC<GameListFilterProps> = ({ onSearch, onOrderChange, onClearFilter, totalGames, initialSearchQuery, initialOrder, onPriorityModeChange, priorityMode = false, currentStatus = 1 }) => {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [order, setOrder] = useState(initialOrder);
  const [isPriorityActive, setIsPriorityActive] = useState(initialOrder === 'priority_desc');
  const [previousStatus, setPreviousStatus] = useState(currentStatus);
  const [wasActivePriority, setWasActivePriority] = useState(false);
  const [wasEditMode, setWasEditMode] = useState(false);
  const theme = useTheme();

  // デバウンス処理を行う関数を作成
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      onSearch(query);
    }, 1000),
    [onSearch]
  );

  // 初期値の更新を監視
  useEffect(() => {
    setSearchQuery(initialSearchQuery);
    setOrder(initialOrder);
    setIsPriorityActive(initialOrder === 'priority_desc');
  }, [initialSearchQuery, initialOrder]);

  // ステータス変更時の処理を追加
  useEffect(() => {
    if (currentStatus !== previousStatus) {
      if (currentStatus === 1 || currentStatus === 2) {
        // ステータス1,2に変更された場合
        if (order === 'priority_desc') {
          setWasActivePriority(true);
          setOrder('default');
          onOrderChange('default');
        }
        // エディットモードの状態を保存してOFFに
        if (priorityMode) {
          setWasEditMode(true);
          onPriorityModeChange?.(false);
        }
      } else if (previousStatus === 1 || previousStatus === 2) {
        // ステータス1,2から他のステータスに変更された場合
        if (wasActivePriority) {
          setOrder('priority_desc');
          onOrderChange('priority_desc');
          setIsPriorityActive(true);
          setWasActivePriority(false);
        }
        // エディットモードを元の状態に戻す
        if (wasEditMode) {
          onPriorityModeChange?.(true);
          setWasEditMode(false);
        }
      }
      setPreviousStatus(currentStatus);
    }
  }, [currentStatus, order, onOrderChange, priorityMode, onPriorityModeChange]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const handleOrderChange = (event: SelectChangeEvent) => {
    const newOrder = event.target.value;
    // ステータス1,2の場合、プライオリティ順は選択できないようにする
    if ((currentStatus === 1 || currentStatus === 2) && newOrder === 'priority_desc') {
      return;
    }
    setOrder(newOrder);
    onOrderChange(newOrder);
    setIsPriorityActive(false);
  };

  const handlePriorityClick = () => {
    // ステータス1,2の場合、プライオリティ順は選択できないようにする
    if (currentStatus === 1 || currentStatus === 2) {
      return;
    }
    
    // プライオリティボタンの状態を切り替え
    const newPriorityActive = !isPriorityActive;
    setIsPriorityActive(newPriorityActive);
    
    // 並び順を更新
    const newOrder = newPriorityActive ? 'priority_desc' : 'default';
    setOrder(newOrder);
    onOrderChange(newOrder);
  };

  const clearFilter = () => {
    setSearchQuery('');
    setOrder('default');
    setIsPriorityActive(false);
    onClearFilter();
  };

  // 検索クエリが空でない場合のみフィルターがアクティブとみなす
  const isFilterActive = searchQuery !== '';

  // orderの表示テキストをマッピング
  const orderDisplayText: { [key: string]: string } = {
    'default': 'デフォルト',
    'rating': '評価 (低い順)',
    'rating_desc': '評価 (高い順)',
    'play_time': 'プレイ時間 (短い順)',
    'play_time_desc': 'プレイ時間 (長い順)',
    'play_start_date': 'プレイ開始日 (古い順)',
    'play_start_date_desc': 'プレイ開始日 (新しい順)',
    'release_date': '発売日 (古い順)',
    'release_date_desc': '発売日 (新しい順)',
    'added_date': '追加日 (古い順)',
    'added_date_desc': '追加日 (新しい順)',
    'priority_desc': 'PRIORITY',
    'priority': '優先度 (低い順)'
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          placeholder="Filter..."
          variant="standard"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            flexGrow: 1,
            '& .MuiInputBase-input': {
              ...theme.typography.body2,
            },
            '& .MuiInputBase-input::placeholder': {
              opacity: 0.5
            }
          }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title={currentStatus === 1 || currentStatus === 2 ? "プライオリティは積みゲー/中断/ウィッシュリストで適用できます" : "プライオリティ"}>
            <StyledPriorityButton
              onClick={handlePriorityClick}
              size="small"
              className={isPriorityActive ? 'active' : ''}
              disabled={currentStatus === 1 || currentStatus === 2}
              sx={{
                opacity: currentStatus === 1 || currentStatus === 2 ? 0.5 : 1,
              }}
            >
              <AutoAwesomeIcon 
                sx={{ 
                  color: isPriorityActive ? '#fff' : 'text.secondary',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  filter: isPriorityActive ? 'drop-shadow(0 0 2px rgba(255,255,255,0.5))' : 'none',
                }} 
              />
            </StyledPriorityButton>
          </Tooltip>
          {onPriorityModeChange && (
            <Tooltip title={currentStatus === 1 || currentStatus === 2 ? "エディットモードは積みゲー/中断/ウィッシュリストで適用できます" : "エディットモード"}>
              <IconButton
                size="small"
                onClick={() => onPriorityModeChange(!priorityMode)}
                sx={{
                  backgroundColor: priorityMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '4px',
                  padding: '6px',
                  opacity: currentStatus === 1 || currentStatus === 2 ? 0.5 : 1,
                }}
                disabled={currentStatus === 1 || currentStatus === 2}
              >
                <TuneIcon 
                  sx={{ 
                    color: priorityMode ? 'text.primary' : 'text.secondary',
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                  }} 
                />
              </IconButton>
            </Tooltip>
          )}
          <Select
            value={order}
            onChange={handleOrderChange}
            displayEmpty
            sx={{
              minWidth: 100,
              ...theme.typography.body2,
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              borderRadius: '4px',
              '& .MuiSelect-select': {
                paddingY: 0.5,
                paddingX: 1,
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                fontSize: { xs: '0.65rem', sm: '0.8rem' },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              }
            }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SortIcon sx={{ 
                  mr: 0.5, 
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  display: { xs: 'none', sm: 'block' }
                }} />
                {orderDisplayText[selected] || '並び替え'}
              </Box>
            )}
          >
            <MenuItem value="default" dense>デフォルト</MenuItem>
            <MenuItem value="rating_desc" dense>評価 (高い順)</MenuItem>
            <MenuItem value="rating" dense>評価 (低い順)</MenuItem>
            <MenuItem value="play_time_desc" dense>プレイ時間 (長い順)</MenuItem>
            <MenuItem value="play_time" dense>プレイ時間 (短い順)</MenuItem>
            <MenuItem value="play_start_date_desc" dense>プレイ開始日 (新しい順)</MenuItem>
            <MenuItem value="play_start_date" dense>プレイ開始日 (古い順)</MenuItem>
            <MenuItem value="release_date_desc" dense>発売日 (新しい順)</MenuItem>
            <MenuItem value="release_date" dense>発売日 (古い順)</MenuItem>
            <MenuItem value="added_date_desc" dense>追加日 (新しい順)</MenuItem>
            <MenuItem value="added_date" dense>追加日 (古い順)</MenuItem>
            {/* <MenuItem value="priority_desc" dense>優先度 (高い順)</MenuItem>
            <MenuItem value="priority" dense>優先度 (低い順)</MenuItem> */}
          </Select>
          <Tips
                title="TIPS:プライオリティ"
                content={<PriorityTipsContent />}
                buttonComponent={
                  <IconButton
                    size="small"
                    sx={{
                      padding: '4px',
                      opacity: 0.7,
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                }
              />
        </Box>
      </Box>
      {isFilterActive && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {totalGames}件のゲームが見つかりました
          </Typography>
          <Button
            size="small"
            onClick={clearFilter}
            startIcon={<CloseIcon fontSize="small" />}
            sx={{ ...theme.typography.body2, textTransform: 'none', p: '2px 8px' }}
          >
            Clear filter
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default GameListFilter;
